import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {Card, Descriptions, Button, Spin, Alert, DatePicker, Input, Modal, Form, message, Select} from "antd";
import api from "../../api";
import {LeftOutlined, PrinterOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import 'jspdf-autotable';
import logo from "../../assets/cc-junket-receipt.png";
import {boldLabelStyle, maxW700, NoWrap} from "../../styles/styles";

const {Option} = Select;

declare module "jspdf" {
    interface jsPDF {
        lastAutoTable?: {
            finalY: number;
        };
    }
}

const TripDetailsPage: React.FC = () => {
    const navigate = useNavigate();
    const {id} = useParams<{ id: string }>();
    const [trip, setTrip] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [form] = Form.useForm();
    const selectedCurrency = Form.useWatch("exchange_currency", form);
    const [exchangeRates, setExchangeRates] = useState<any>({});

    const parseMoneyInput = (value: any): number => {
        if (value === null || value === undefined || value === "") return 0;
        return Number(value.toString().replace(/,/g, "")) || 0;
    };

    const airfareExpense = Form.useWatch("airfare_expense", form);
    const roomExpense = Form.useWatch("room_expense", form);
    const fbExpense = Form.useWatch("fb_expense", form);
    const otherExpense = Form.useWatch("other_expense", form);

    const totalExpensePreview =
        parseMoneyInput(airfareExpense) +
        parseMoneyInput(roomExpense) +
        parseMoneyInput(fbExpense) +
        parseMoneyInput(otherExpense);

    useEffect(() => {
        fetchTripDetails();
        fetchExchangeRates();
    }, [id]);

    // const handleDownloadPDF = () => {
    //     const doc = new jsPDF();
    //
    //     const pageWidth = doc.internal.pageSize.getWidth();
    //     const today = new Date().toLocaleDateString();
    //
    //     // Logo setup
    //     const desiredWidth = 60;
    //     const originalWidth = 500;
    //     const originalHeight = 285;
    //     const aspectRatio = originalHeight / originalWidth;
    //     const scaledHeight = desiredWidth * aspectRatio;
    //     const imgX = (pageWidth - desiredWidth) / 2;
    //
    //     // Add logo centered
    //     doc.addImage(logo, "PNG", imgX, 10, desiredWidth, scaledHeight);
    //
    //     // Title
    //     doc.setFontSize(16);
    //     doc.setFont("helvetica", "bold");
    //     doc.text("DAIA Junket Trip Receipt", 14, 60);
    //
    //     // Date in top right corner
    //     doc.setFontSize(10);
    //     doc.setFont("helvetica", "normal");
    //     doc.text(`Date: ${today}`, pageWidth - 60, 60);
    //
    //     const formatKRW = (value: number | string | null | undefined) =>
    //         value !== null && value !== undefined && !isNaN(Number(value))
    //             ? `KRW ${parseFloat(value.toString()).toLocaleString()}`
    //             : "N/A";
    //
    //     // Table data (excluding Agent Fee)
    //     const data = [
    //         ["Customer Name", `${trip.customer.first_name} ${trip.customer.last_name}`],
    //         ["MMID", trip.customer.mmid],
    //         ["Agent Name", trip.agent ? `${trip.agent.first_name} ${trip.agent.last_name}` : "Casino Club"],
    //         ["Agent Program", trip.agent?.program || "N/A"],
    //         ["Check-in Date", trip.checkin_date || "N/A"],
    //         ["Checkout Date", trip.checkout_date || "N/A"],
    //         ["Buy-in Amount", formatKRW(trip.buyin_amount)],
    //         ["Rebuy Amount", formatKRW(trip.rebuys)],
    //         ["Final Balance", formatKRW(trip.final_balance)],
    //         ["Win / Loss", formatKRW(trip.win_loss)],
    //         ["Junket Share", formatKRW(trip.junket_share)],
    //         ["Bonus Structure", trip.bonus_structure || "N/A"],
    //         ["Rolling Amount", formatKRW(trip.rolling_amount)],
    //         ["Bonus Amount", formatKRW(trip.bonus_amount)],
    //         ["Expenses", formatKRW(trip.expenses)],
    //         ["Memo", formatKRW(trip.memo)]
    //     ];
    //
    //     // Table
    //     autoTable(doc, {
    //         startY: 70,
    //         head: [["Description", "Details"]],
    //         body: data,
    //         styles: {fontSize: 10},
    //         headStyles: {fillColor: [41, 128, 185]},
    //     });
    //
    //     // Emphasized Agent Fee
    //     const agentFeeY = doc.lastAutoTable?.finalY
    //         ? doc.lastAutoTable.finalY + 15
    //         : 85; // fallback if table rendering failed
    //
    //     const agentFee = parseFloat(trip.agent_fees);
    //     doc.setFontSize(14);
    //     doc.setFont("helvetica", "bold");
    //     doc.text("Agent Fee", 14, agentFeeY);
    //
    //     if (agentFee < 0) {
    //         // Deep red for negative fee (agent owes us)
    //         doc.setTextColor(192, 57, 43);
    //     } else {
    //         // Black for positive fee (we pay agent)
    //         doc.setTextColor(0, 0, 0);
    //     }
    //
    //     doc.text(formatKRW(agentFee), 14, agentFeeY + 8);
    //
    //     // Reset
    //     doc.setFont("helvetica", "normal");
    //     doc.setTextColor(0, 0, 0);
    //
    //     // Save PDF
    //     doc.save(`Trip_${trip.customer.mmid}_Receipt.pdf`);
    // };


    const fetchTripDetails = async () => {
        try {
            const res = await api.get(`/daia/trips/${id}/`);
            setTrip(res.data);
        } catch (err) {
            setError("Failed to fetch trip details. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const fetchExchangeRates = async () => {
        try {
            const res = await api.get("/dolphin/rates/?rate_type=cc");
            setExchangeRates(res.data[0]);
        } catch (err) {
            console.error("Failed to fetch exchange rates");
        }
    };

    const formatNumberInput = (value: string): string => {
        const numericValue = value.replace(/,/g, "");
        if (!isNaN(Number(numericValue))) {
            return Number(numericValue).toLocaleString(undefined, {maximumFractionDigits: 0});
        }
        return "";
    };

    const formatMoney = (value: any): string => {
        const num = parseMoneyInput(value);
        return num.toLocaleString(undefined, {maximumFractionDigits: 0});
    };

    const handleEdit = () => {
        setIsEditing(true);
        form.setFieldsValue({
            checkin_date: trip.checkin_date ? dayjs(trip.checkin_date) : null,
            checkout_date: trip.checkout_date ? dayjs(trip.checkout_date) : null,
            rebuys: trip.rebuys?.toLocaleString() || "",
            final_balance: trip.final_balance?.toLocaleString() || "",
            rolling_amount: trip.rolling_amount?.toLocaleString() || "",
            airfare_expense: trip.airfare_expense?.toLocaleString() || "",
            room_expense: trip.room_expense?.toLocaleString() || "",
            fb_expense: trip.fb_expense?.toLocaleString() || "",
            other_expense: trip.other_expense?.toLocaleString() || "",
            expenses: trip.expenses?.toLocaleString() || "",
            buyin_amount: trip.buyin_amount?.toLocaleString() || "",
            exchange_currency: trip.exchange_currency || "KRW",
            exchange_rate: trip.exchange_rate || null,
            memo: trip.memo || null
        });
    };

    const handleCurrencyChange = (currency: string) => {
        const amountStr = form.getFieldValue("exchange_amount") || "0";
        const amount = parseFloat(amountStr.replace(/,/g, ""));
        let rate = 0;

        if (currency === "KRW") {
            form.setFieldsValue({exchange_rate: null, exchange_amount: null});
            return;
        }

        if (currency === "USDT-TRC") {
            rate = exchangeRates.usdt_trc_buy || 0;
        } else if (currency === "USDT-ERC") {
            rate = exchangeRates.usdt_erc_buy || 0;
        } else {
            const field = `${currency.toLowerCase()}_buy`;
            rate = exchangeRates[field] || 0;
        }

        const buyin = (!isNaN(amount) && !isNaN(rate)) ? amount * rate : 0;

        form.setFieldsValue({
            exchange_rate: rate,
            buyin_amount: buyin.toLocaleString(undefined, {maximumFractionDigits: 0}),
        });
    };


    const handleUpdate = async (values: any) => {
        try {
            await api.patch(`/daia/trips/${id}/`, {
                checkin_date: values.checkin_date?.format("YYYY-MM-DD") || null,
                checkout_date: values.checkout_date ? values.checkout_date.format("YYYY-MM-DD") : null,
                rebuys: values.rebuys ? parseFloat(values.rebuys.replace(/,/g, "")) : 0,
                final_balance: values.final_balance ? parseFloat(values.final_balance.replace(/,/g, "")) : null,
                rolling_amount: values.rolling_amount ? parseFloat(values.rolling_amount.replace(/,/g, "")) : null,
                airfare_expense: values.airfare_expense ? parseFloat(values.airfare_expense.replace(/,/g, "")) : 0,
                room_expense: values.room_expense ? parseFloat(values.room_expense.replace(/,/g, "")) : 0,
                fb_expense: values.fb_expense ? parseFloat(values.fb_expense.replace(/,/g, "")) : 0,
                other_expense: values.other_expense ? parseFloat(values.other_expense.replace(/,/g, "")) : 0,
                buyin_amount: values.buyin_amount ? parseFloat(values.buyin_amount.replace(/,/g, "")) : 0,
                exchange_currency: values.exchange_currency || null,
                exchange_rate: values.exchange_currency !== "KRW" ? values.exchange_rate : null,
                memo: values.memo || null,
            });
            message.success("Trip updated successfully.");
            setIsEditing(false);
            fetchTripDetails();
        } catch (err) {
            message.error("Failed to update trip.");
        }
    };

    if (loading) return <Spin size="large"/>;
    if (error) return <Alert message={error} type="error" showIcon/>;

    const showRollingAmountField = trip.bonus_structure === "Rolling" || ["Rolling", "Hybrid"].includes(trip.agent?.program);

    return (
        <Card
            title={
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <Button icon={<LeftOutlined/>} onClick={() => navigate(-1)}>
                        Back
                    </Button>
                    <div style={{display: "flex", gap: 8}}>
                        {/*{!isNaN(parseFloat(trip.junket_share)) && parseFloat(trip.agent_fees) !== 0 && (*/}
                        {/*    <Button*/}
                        {/*        icon={<PrinterOutlined/>}*/}
                        {/*        onClick={handleDownloadPDF}*/}
                        {/*        type="default"*/}
                        {/*        danger*/}
                        {/*        ghost*/}
                        {/*    >*/}
                        {/*        PDF Receipt*/}
                        {/*    </Button>*/}
                        {/*)}*/}
                        <Button type="primary" onClick={handleEdit}>Edit Trip</Button>
                    </div>
                </div>
            }
            style={{maxWidth: 800, margin: "20px auto", overflowX: "auto"}}
        >
            <Descriptions bordered column={1} size="middle" style={maxW700}>
                <Descriptions.Item label="Customer" labelStyle={boldLabelStyle}>
                    <NoWrap>{trip.customer.first_name} {trip.customer.last_name} ({trip.customer.nationality})</NoWrap>
                </Descriptions.Item>
                <Descriptions.Item label="MMID"
                                   labelStyle={boldLabelStyle}><NoWrap>{trip.customer.mmid}</NoWrap></Descriptions.Item>
                <Descriptions.Item label="Phone"
                                   labelStyle={boldLabelStyle}><NoWrap>{trip.customer.phone || "N/A"}</NoWrap></Descriptions.Item>
                <Descriptions.Item label="Buy-in Method"
                                   labelStyle={boldLabelStyle}><NoWrap>{trip.buy_in_method || "N/A"}</NoWrap></Descriptions.Item>
                <Descriptions.Item label="Agent" labelStyle={boldLabelStyle}>
                    <NoWrap>{trip.agent ? `${trip.agent.first_name} ${trip.agent.last_name} (${trip.agent.nationality})` : "Casino Club"}</NoWrap>
                </Descriptions.Item>
                <Descriptions.Item label="Agent Program"
                                   labelStyle={boldLabelStyle}><NoWrap>{trip.agent?.program || "N/A"}</NoWrap></Descriptions.Item>
                <Descriptions.Item label="Check-in Date"
                                   labelStyle={boldLabelStyle}><NoWrap>{trip.checkin_date}</NoWrap></Descriptions.Item>
                <Descriptions.Item label="Check-out Date"
                                   labelStyle={boldLabelStyle}><NoWrap>{trip.checkout_date || "Ongoing"}</NoWrap></Descriptions.Item>
                <Descriptions.Item
                    label="Buy-in Amount"
                    labelStyle={boldLabelStyle}><NoWrap>₩{parseFloat(trip.buyin_amount).toLocaleString(undefined, {maximumFractionDigits: 0})}</NoWrap></Descriptions.Item>
                <Descriptions.Item
                    label="Rebuys"
                    labelStyle={boldLabelStyle}><NoWrap>₩{parseFloat(trip.rebuys).toLocaleString(undefined, {maximumFractionDigits: 0})}</NoWrap></Descriptions.Item>
                <Descriptions.Item label="Final Balance" labelStyle={boldLabelStyle}>
                    <NoWrap>{trip.final_balance !== null ? `₩${parseFloat(trip.final_balance).toLocaleString(undefined, {maximumFractionDigits: 0})}` : "Pending"}</NoWrap>
                </Descriptions.Item>
                <Descriptions.Item label="Win/Loss" labelStyle={boldLabelStyle}>
      <span style={{color: parseFloat(trip.win_loss) >= 0 ? "green" : "red"}}>
        <NoWrap>₩{parseFloat(trip.win_loss).toLocaleString(undefined, {maximumFractionDigits: 0})}</NoWrap>
      </span>
                </Descriptions.Item>
                <Descriptions.Item label="Junket Share" labelStyle={boldLabelStyle}>
      <span style={{color: parseFloat(trip.junket_share) >= 0 ? "green" : "red"}}>
       <NoWrap>₩{parseFloat(trip.junket_share).toLocaleString(undefined, {maximumFractionDigits: 0})}</NoWrap>
      </span>
                </Descriptions.Item>
                <Descriptions.Item label="Bonus Structure"
                                   labelStyle={boldLabelStyle}><NoWrap>{trip.bonus_structure}</NoWrap></Descriptions.Item>
                <Descriptions.Item label="Rolling Amount" labelStyle={boldLabelStyle}>
                    <NoWrap>{trip.rolling_amount ? `₩${parseFloat(trip.rolling_amount).toLocaleString(undefined, {maximumFractionDigits: 0})}` : "N/A"}</NoWrap>
                </Descriptions.Item>
                <Descriptions.Item label="Bonus Amount" labelStyle={boldLabelStyle}>
                    <NoWrap>{trip.bonus_amount ? `₩${parseFloat(trip.bonus_amount).toLocaleString(undefined, {maximumFractionDigits: 0})}` : "N/A"}</NoWrap>
                </Descriptions.Item>
                <Descriptions.Item label="Airfare Expense" labelStyle={boldLabelStyle}>
                    <NoWrap>₩{formatMoney(trip.airfare_expense)}</NoWrap>
                </Descriptions.Item>

                <Descriptions.Item label="Room Expense" labelStyle={boldLabelStyle}>
                    <NoWrap>₩{formatMoney(trip.room_expense)}</NoWrap>
                </Descriptions.Item>

                <Descriptions.Item label="F&B Expense" labelStyle={boldLabelStyle}>
                    <NoWrap>₩{formatMoney(trip.fb_expense)}</NoWrap>
                </Descriptions.Item>

                <Descriptions.Item label="Other Expense" labelStyle={boldLabelStyle}>
                    <NoWrap>₩{formatMoney(trip.other_expense)}</NoWrap>
                </Descriptions.Item>

                <Descriptions.Item label="Total Expenses" labelStyle={boldLabelStyle}>
                    <NoWrap>{trip.expenses !== null ? `₩${formatMoney(trip.expenses)}` : "Pending"}</NoWrap>
                </Descriptions.Item>
                {/*<Descriptions.Item label="Agent Fees" labelStyle={boldLabelStyle}>*/}
                {/*    <NoWrap>{trip.agent_fees !== null ? `₩${parseFloat(trip.agent_fees).toLocaleString(undefined, {maximumFractionDigits: 0})}` : "Pending"}</NoWrap>*/}
                {/*</Descriptions.Item>*/}
                <Descriptions.Item label="P&L" labelStyle={boldLabelStyle}>
                    <NoWrap>{trip.pnl !== null ? (
                        <span style={{color: parseFloat(trip.pnl) >= 0 ? "green" : "red"}}>
          ₩{parseFloat(trip.pnl).toLocaleString(undefined, {maximumFractionDigits: 0})}
        </span>
                    ) : "Pending"}</NoWrap>
                </Descriptions.Item>
                <Descriptions.Item label="Exchange Currency"
                                   labelStyle={boldLabelStyle}><NoWrap>{trip.exchange_currency || "N/A"}</NoWrap></Descriptions.Item>
                <Descriptions.Item label="Exchange Rate"
                                   labelStyle={boldLabelStyle}><NoWrap>{trip.exchange_rate || "N/A"}</NoWrap></Descriptions.Item>
                <Descriptions.Item
                    label="Locked Exchange Rate"
                    labelStyle={boldLabelStyle}><NoWrap>{trip.locked_exchange_rate ? "Yes" : "No"}</NoWrap></Descriptions.Item>
                <Descriptions.Item
                    label="Memo"
                    labelStyle={boldLabelStyle}><NoWrap>{trip.memo}</NoWrap></Descriptions.Item>
            </Descriptions>

            <Modal
                title="Edit Trip"
                open={isEditing}
                onCancel={() => setIsEditing(false)}
                onOk={() => form.submit()}
                okText="Save"
            >
                <Form form={form} layout="vertical" onFinish={handleUpdate}>
                    <Form.Item name="checkin_date" label="Check-in Date">
                        <DatePicker style={{width: "100%"}} format="YYYY-MM-DD"/>
                    </Form.Item>
                    <Form.Item name="checkout_date" label="Check-out Date">
                        <DatePicker style={{width: "100%"}} format="YYYY-MM-DD"/>
                    </Form.Item>

                    <Form.Item name="exchange_currency" label="Buy-in Currency">
                        <Select onChange={handleCurrencyChange}>
                            <Option value="KRW">KRW</Option>
                            <Option value="YEN">YEN</Option>
                            <Option value="USD">USD</Option>
                            <Option value="HKD">HKD</Option>
                            <Option value="USDT-TRC">USDT-TRC</Option>
                            <Option value="USDT-ERC">USDT-ERC</Option>
                        </Select>
                    </Form.Item>

                    {selectedCurrency && selectedCurrency !== "KRW" && (
                        <>
                            <Form.Item
                                name="exchange_amount"
                                label="Exchange Amount"
                                rules={[{message: "Please input the exchange amount"}]}
                            >
                                <Input
                                    type="text"
                                    onChange={(e) => {
                                        const formatted = formatNumberInput(e.target.value);
                                        form.setFieldsValue({exchange_amount: formatted});

                                        const rate = parseFloat((form.getFieldValue("exchange_rate") || "0").toString());
                                        const amount = parseFloat(formatted.replace(/,/g, ""));
                                        if (!isNaN(rate) && !isNaN(amount)) {
                                            form.setFieldsValue({
                                                buyin_amount: (amount * rate).toLocaleString(undefined, {maximumFractionDigits: 0}),
                                            });
                                        }
                                    }}
                                />
                            </Form.Item>

                            <Form.Item name="exchange_rate" label="Exchange Rate">
                                <Input disabled style={{width: "100%"}}/>
                            </Form.Item>
                        </>
                    )}

                    <Form.Item name="buyin_amount" label="Buy-in Amount">
                        <Input prefix="₩" type="text" onChange={(e) => {
                            const formatted = formatNumberInput(e.target.value);
                            form.setFieldsValue({buyin_amount: formatted});
                        }}/>
                    </Form.Item>

                    <Form.Item name="rebuys" label="Rebuys">
                        <Input prefix="₩" type="text" onChange={(e) => {
                            const formatted = formatNumberInput(e.target.value);
                            form.setFieldsValue({rebuys: formatted});
                        }}/>
                    </Form.Item>
                    <Form.Item name="final_balance" label="Final Balance" rules={[{required: true}]}>
                        <Input prefix="₩" type="text" onChange={(e) => {
                            const formatted = formatNumberInput(e.target.value);
                            form.setFieldsValue({final_balance: formatted});
                        }}/>
                    </Form.Item>
                    {showRollingAmountField && (
                        <Form.Item name="rolling_amount" label="Rolling Amount">
                            <Input prefix="₩" type="text" onChange={(e) => {
                                const formatted = formatNumberInput(e.target.value);
                                form.setFieldsValue({rolling_amount: formatted});
                            }}/>
                        </Form.Item>
                    )}
                    <Form.Item name="airfare_expense" label="Airfare Expense">
                        <Input prefix="₩" type="text" onChange={(e) => {
                            const formatted = formatNumberInput(e.target.value);
                            form.setFieldsValue({airfare_expense: formatted});
                        }}/>
                    </Form.Item>

                    <Form.Item name="room_expense" label="Room Expense">
                        <Input prefix="₩" type="text" onChange={(e) => {
                            const formatted = formatNumberInput(e.target.value);
                            form.setFieldsValue({room_expense: formatted});
                        }}/>
                    </Form.Item>

                    <Form.Item name="fb_expense" label="F&B Expense">
                        <Input prefix="₩" type="text" onChange={(e) => {
                            const formatted = formatNumberInput(e.target.value);
                            form.setFieldsValue({fb_expense: formatted});
                        }}/>
                    </Form.Item>

                    <Form.Item name="other_expense" label="Other Expense">
                        <Input prefix="₩" type="text" onChange={(e) => {
                            const formatted = formatNumberInput(e.target.value);
                            form.setFieldsValue({other_expense: formatted});
                        }}/>
                    </Form.Item>

                    <Form.Item label="Total Expenses">
                        <Input
                            prefix="₩"
                            value={totalExpensePreview.toLocaleString(undefined, {maximumFractionDigits: 0})}
                            disabled
                        />
                    </Form.Item>
                    <Form.Item label="Memo" name="memo"><Input.TextArea rows={4}/></Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default TripDetailsPage;
